import React, { Fragment } from 'react';
import { TrendingUp, TrendingDown, Sparkles, RefreshCw, Edit3 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { articles, dashboardKPIs, coverageByTier, sentimentOverTime, topSources, spokespersonMentions, executiveSummary, tierLabels, sentimentLabels } from '../lib/data';
function getSentimentColor(sentiment: string) {
  switch (sentiment) {
    case 'positive':
      return 'bg-sentiment-positive';
    case 'neutral':
      return 'bg-sentiment-neutral';
    case 'negative':
      return 'bg-sentiment-negative';
    default:
      return 'bg-muted';
  }
}
function getTierColor(tier: number | string) {
  switch (tier) {
    case 1:
      return 'bg-tier-1 text-white';
    case 2:
      return 'bg-tier-2 text-white';
    case 3:
      return 'bg-tier-3 text-white';
    case 'specialized':
      return 'bg-tier-special text-white';
    default:
      return 'bg-muted';
  }
}
function KPI({
  label,
  value,
  trend,
  accent = false
}: {
  label: string;
  value: string | number;
  trend?: number;
  accent?: boolean;
}) {
  return <div className="flex flex-col gap-2" data-id="element-456">
      <div className="text-sm text-muted-foreground" data-id="element-457">{label}</div>
      <div className={`text-3xl md:text-4xl font-semibold leading-none ${accent ? 'text-accent' : 'text-foreground'}`} style={{
      fontVariantNumeric: 'tabular-nums'
    }} data-id="element-458">
        {value}
      </div>
      {trend !== undefined && <div className="flex items-center gap-1.5 text-sm" data-id="element-459">
          {trend > 0 ? <TrendingUp className="h-3.5 w-3.5 text-sentiment-positive" data-id="element-460" /> : <TrendingDown className="h-3.5 w-3.5 text-sentiment-negative" data-id="element-461" />}
          <span className={trend > 0 ? 'text-sentiment-positive' : 'text-sentiment-negative'} data-id="element-462">
            {trend > 0 ? '+' : ''}
            {trend}%
          </span>
          <span className="text-muted-foreground" data-id="element-463">vs prec.</span>
        </div>}
    </div>;
}
export function Dashboard() {
  const topArticles = articles.filter(a => a.status === 'approved').slice(0, 5);
  return <div className="p-6 max-w-[1400px] mx-auto space-y-8" data-id="element-464">
      {/* Page header */}
      <header data-id="element-465">
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight" data-id="element-466">
          Dashboard
        </h1>
      </header>

      {/* KPI strip */}
      <section className="bg-card border border-border rounded-lg p-6 grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10" data-id="element-467">
        <KPI label="Articoli totali" value={dashboardKPIs.totalArticles.value} trend={dashboardKPIs.totalArticles.trend} data-id="element-468" />
        <KPI label="Score medio" value={dashboardKPIs.avgRelevanceScore.value.toFixed(1)} accent data-id="element-469" />
        <KPI label="AVE totale" value={`€${(dashboardKPIs.totalAVE.value / 1000).toFixed(0)}k`} trend={dashboardKPIs.totalAVE.trend} data-id="element-470" />
        <div className="flex flex-col gap-2" data-id="element-471">
          <div className="text-sm text-muted-foreground" data-id="element-472">Sentiment</div>
          <div className="flex items-baseline gap-2 leading-none" data-id="element-473">
            <span className="text-2xl md:text-3xl font-semibold text-sentiment-positive" style={{
            fontVariantNumeric: 'tabular-nums'
          }} data-id="element-474">
              {dashboardKPIs.sentimentMix.positive}
            </span>
            <span className="text-sm text-muted-foreground" data-id="element-475">/</span>
            <span className="text-xl text-muted-foreground" style={{
            fontVariantNumeric: 'tabular-nums'
          }} data-id="element-476">
              {dashboardKPIs.sentimentMix.neutral}
            </span>
            <span className="text-sm text-muted-foreground" data-id="element-477">/</span>
            <span className="text-xl text-sentiment-negative" style={{
            fontVariantNumeric: 'tabular-nums'
          }} data-id="element-478">
              {dashboardKPIs.sentimentMix.negative}
            </span>
          </div>
          <div className="text-sm text-muted-foreground" data-id="element-479">
            Positivi / neutri / negativi
          </div>
        </div>
      </section>

      {/* Executive Summary */}
      <section className="bg-card border border-border rounded-lg p-6" data-id="element-480">
        <div className="border-l-2 border-accent pl-5 md:pl-6" data-id="element-481">
          <div className="flex items-center justify-between mb-3" data-id="element-482">
            <div className="flex items-center gap-2 text-sm text-muted-foreground" data-id="element-483">
              <Sparkles className="h-3.5 w-3.5 text-accent" data-id="element-484" />
              <span data-id="element-485">Riepilogo AI</span>
            </div>
            <div className="flex items-center gap-1" data-id="element-486">
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground" data-id="element-487">
                <RefreshCw className="h-3 w-3 md:mr-1.5" data-id="element-488" />
                <span className="hidden md:inline" data-id="element-489">Rigenera</span>
              </Button>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground" data-id="element-490">
                <Edit3 className="h-3 w-3 md:mr-1.5" data-id="element-491" />
                <span className="hidden md:inline" data-id="element-492">Modifica</span>
              </Button>
            </div>
          </div>
          <p className="text-base leading-relaxed text-foreground" data-id="element-493">
            {executiveSummary}
          </p>
        </div>
      </section>

      {/* Main content: articles + analytics */}
      <section className="bg-card border border-border rounded-lg p-6 grid grid-cols-1 lg:grid-cols-3 gap-10 md:gap-12" data-id="element-494">
        {/* Left: Top articles */}
        <div className="lg:col-span-2" data-id="element-495">
          <div className="flex items-baseline justify-between mb-5" data-id="element-496">
            <h2 className="text-lg font-semibold" data-id="element-497">Top articoli</h2>
            <Button variant="link" size="sm" className="text-xs text-muted-foreground h-auto p-0" data-id="element-498">
              Vedi tutti
            </Button>
          </div>

          <div className="space-y-1" data-id="element-499">
            {topArticles.map((article, i) => <Fragment key={article.id} data-id="element-500">
                {i > 0 && <Separator data-id="element-501" />}
                <button className="w-full text-left py-4 md:py-5 group hover:bg-muted/30 -mx-3 px-3 rounded-md transition-colors" data-id="element-502">
                  <div className="flex items-start gap-4" data-id="element-503">
                    <img src={article.sourceLogo} alt={article.source} className="w-9 h-9 rounded flex-shrink-0 mt-0.5" data-id="element-504" />

                    <div className="flex-1 min-w-0" data-id="element-505">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5 text-xs text-muted-foreground" data-id="element-506">
                        <Badge className={`text-[10px] px-1.5 py-0 border-0 ${getTierColor(article.tier)}`} data-id="element-507">
                          {tierLabels[article.tier]}
                        </Badge>
                        <span data-id="element-508">{article.source}</span>
                        <span data-id="element-509">·</span>
                        <span data-id="element-510">
                          {new Date(article.date).toLocaleDateString('it-IT', {
                        day: 'numeric',
                        month: 'short'
                      })}
                        </span>
                      </div>

                      <h3 className="font-medium text-sm md:text-base leading-snug mb-2 line-clamp-2" data-id="element-511">
                        {article.title}
                      </h3>

                      <div className="flex items-center gap-5 text-sm" data-id="element-512">
                        <div className="flex items-baseline gap-1.5" data-id="element-513">
                          <span className="font-semibold text-foreground" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-514">
                            {article.relevanceScore.toFixed(1)}
                          </span>
                          <span className="text-xs text-muted-foreground" data-id="element-515">
                            score
                          </span>
                        </div>
                        <div className="flex items-baseline gap-1.5 text-muted-foreground" data-id="element-516">
                          <span style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-517">
                            €{(article.ave / 1000).toFixed(1)}k
                          </span>
                          <span className="text-xs" data-id="element-518">AVE</span>
                        </div>
                        <div className={`w-1.5 h-1.5 rounded-full ${getSentimentColor(article.sentiment)}`} title={sentimentLabels[article.sentiment]} data-id="element-519" />
                      </div>
                    </div>
                  </div>
                </button>
              </Fragment>)}
          </div>
        </div>

        {/* Right: unified analytics card */}
        <div className="space-y-8 md:space-y-10" data-id="element-520">
          {/* Sentiment over time */}
          <div data-id="element-521">
            <h3 className="text-sm font-semibold mb-4" data-id="element-522">Sentiment nel tempo</h3>
            <div className="h-40" data-id="element-523">
              <ResponsiveContainer width="100%" height="100%" data-id="element-524">
                <AreaChart data={sentimentOverTime} margin={{
                top: 5,
                right: 0,
                bottom: 0,
                left: -20
              }} data-id="element-525">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E5" vertical={false} data-id="element-526" />
                  <XAxis dataKey="date" tick={{
                  fontSize: 10
                }} stroke="#8B8680" axisLine={false} tickLine={false} data-id="element-527" />
                  <YAxis tick={{
                  fontSize: 10
                }} stroke="#8B8680" axisLine={false} tickLine={false} data-id="element-528" />
                  <Area type="monotone" dataKey="positive" stackId="1" stroke="#4A7C59" fill="#4A7C59" fillOpacity={0.5} strokeWidth={1.5} data-id="element-529" />
                  <Area type="monotone" dataKey="neutral" stackId="1" stroke="#8B8680" fill="#8B8680" fillOpacity={0.35} strokeWidth={1.5} data-id="element-530" />
                  <Area type="monotone" dataKey="negative" stackId="1" stroke="#B85450" fill="#B85450" fillOpacity={0.5} strokeWidth={1.5} data-id="element-531" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <Separator data-id="element-532" />

          {/* Coverage by Tier */}
          <div data-id="element-533">
            <h3 className="text-sm font-semibold mb-4" data-id="element-534">Copertura per tier</h3>
            <div className="space-y-3" data-id="element-535">
              {coverageByTier.map(item => <div key={item.tier} data-id="element-536">
                  <div className="flex items-center justify-between mb-1.5 text-sm" data-id="element-537">
                    <span className="text-foreground" data-id="element-538">{item.tier}</span>
                    <span className="text-muted-foreground" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-539">
                      {item.count}
                    </span>
                  </div>
                  <div className="h-1 bg-muted rounded-full overflow-hidden" data-id="element-540">
                    <div className="h-full bg-foreground/80 rounded-full" style={{
                  width: `${item.percentage}%`
                }} data-id="element-541" />
                  </div>
                </div>)}
            </div>
          </div>

          <Separator data-id="element-542" />

          {/* Top Sources */}
          <div data-id="element-543">
            <h3 className="text-sm font-semibold mb-4" data-id="element-544">Top testate</h3>
            <div className="space-y-2.5" data-id="element-545">
              {topSources.map(source => <div key={source.name} className="flex items-center justify-between text-sm" data-id="element-546">
                  <span className="text-foreground truncate pr-2" data-id="element-547">
                    {source.name}
                  </span>
                  <div className="flex items-center gap-3 text-muted-foreground flex-shrink-0" data-id="element-548">
                    <span style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-549">
                      {source.articles}
                    </span>
                    <span className="text-foreground font-medium" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-550">
                      {source.avgScore.toFixed(1)}
                    </span>
                  </div>
                </div>)}
            </div>
          </div>

          <Separator data-id="element-551" />

          {/* Spokespersons */}
          <div data-id="element-552">
            <h3 className="text-sm font-semibold mb-4" data-id="element-553">Portavoce citati</h3>
            <div className="space-y-3" data-id="element-554">
              {spokespersonMentions.map(person => <div key={person.name} className="flex items-center justify-between text-sm" data-id="element-555">
                  <div className="min-w-0 pr-2" data-id="element-556">
                    <div className="text-foreground truncate" data-id="element-557">
                      {person.name}
                    </div>
                    <div className="text-xs text-muted-foreground" data-id="element-558">
                      {person.role}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0" data-id="element-559">
                    <span className="font-medium" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-560">
                      {person.mentions}
                    </span>
                    <div className={`w-1.5 h-1.5 rounded-full ${getSentimentColor(person.avgSentiment)}`} data-id="element-561" />
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>
    </div>;
}